import Link from "@mui/icons-material/Link";
import LinkOpen from "@mui/icons-material/LinkOff";
import { Grid, useTheme, styled } from "@mui/material";
import { ScrollBarClick } from "@vmosolutions/oc-interfaces";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "src/components/IconButton/IconButton";
import { IHoveredEvent } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { PairingsGL } from "src/pages/CrewSchedule/gl/PairingsGL";
import { IPairingMap, WaterfallGL } from "src/pages/CrewSchedule/gl/WaterfallGL";
import { IRowItem } from "src/pages/PairingWaterfall/PairingWaterfallList";
import { handleWheel } from "src/pages/PairingWaterfall/WaterfallInputHandler";
import { setHoveredEvents, setTimelineLink } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { findPairingsFromTime, getOverlappingEvents } from "src/redux/actions/CrewSchedule/pairings";
import { setHoveredTime, setInteractiveState } from "src/redux/actions/flights/FlightTimeline.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { defaultCrewBase, defaultTimezone, IBaseTime } from "src/redux/reducers/CrewScheduleReducer";
import { InteractiveState } from "src/redux/reducers/FlightTimelineReducer";
import { BaseTimelineGL } from "../../SharedGL/BaseTimelineGL";
import { HoveredCrewSchedEvents } from "../CrewSchedule/HoveredCrewSchedEvents";
import { FlightTimelineDatePicker } from "../FlightTimeline/FlightTimelineDatePicker";

interface IPairingWaterfallCanvasProps {
  id: string;
  path: string[];
  itemList: IRowItem[];
  itemListRef: React.MutableRefObject<HTMLDivElement>;
  pairingMap: IPairingMap;
  initialStartTime: number;
  initialEndTime: number;
  setCurrentRange: any;
  currentRange: number[];
  setScrollToDate?: React.Dispatch<React.SetStateAction<(jumpDate: Date, toFront?: boolean) => void>>;
}

export interface IAnchorSelection {
  anchorPos: { x: number; y: number };
  crewId: string;
  time: number;
}

export interface ICrewMemberGLDimensions {
  rowStart: number;
  rowHeight: number;
  isExpanded: boolean;
}

const OCPairingWaterfallCanvas = styled(Grid)(({ theme }) => ({
  idth: "100%",
  flexGrow: 1,
  minHeight: "0px",
  minWidth: "0px",
  flexBasis: "0px",
  overflow: "hidden",
  "& .crew_schedGL": {
    width: "100%",
    flexGrow: 1,
    minHeight: "0px",
    minWidth: "0px",
    flexBasis: "0px",
    overflow: "hidden",
    position: "relative",
  },
}));

let isAnimating = false;

export const PairingWaterfallCanvas = (props: IPairingWaterfallCanvasProps): JSX.Element => {
  const mount = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number>(0);
  const dispatch = useDispatch();
  const {
    id,
    itemList,
    itemListRef,
    pairingMap,
    initialStartTime,
    initialEndTime,
    currentRange,
    setCurrentRange,
    setScrollToDate,
  } = props;

  const theme = useTheme();
  const waterfallGL = useRef<WaterfallGL | null>(null);

  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const crewBase = windowProperties?.crewBase ?? defaultCrewBase;
  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;
  // const focusCrewSchedArea = windowProperties?.focusCrewSchedArea;
  const shouldDeselectRef = useRef<boolean>(true);
  const canUpdateHoveredEvent = useRef<boolean>(true);
  const [captureMode, setCaptureMode] = useState<boolean>(false);
  const [pointerId, setPointerId] = useState<number>(0);

  const interactiveState = useSelector((state: IRootState) => state.timelineFlights.interactiveState[id]);
  const [showTimelineDatePickers, setShowTimelineDatePickers] = useState<boolean>(false);

  // type StartFunction = () => void;
  let clickedOnScroll: ScrollBarClick = 1;

  const exitCaptureMode = (): void => {
    if (pointerId) mount?.current?.releasePointerCapture(pointerId);
    setCaptureMode(false);
    setPointerId(0);
  };

  const enterCaptureMode = (pointerId: number): void => {
    setPointerId(pointerId);
    mount?.current?.setPointerCapture(pointerId);
    setCaptureMode(true);
  };

  const handleMouseDown = (event: React.MouseEvent): void => {
    isAnimating = true;
    shouldDeselectRef.current = true;
    // interactionMode = InteractiveState.Pan;

    const yPos = event.clientY - mount?.current?.getBoundingClientRect()?.top;
    if (yPos > 0 && yPos < 60) {
      // If user clicks on top 60 pixels of the timeline (the time axis), display the date range pickers.
      setShowTimelineDatePickers(true);
    }

    start();

    //if you are clicking on the scroll bar - move it
    const rect = mount?.current?.getBoundingClientRect();

    clickedOnScroll = waterfallGL?.current?.scrollClick(
      (event.clientX - rect.left) * window.devicePixelRatio,
      event.clientY - rect.top - BaseTimelineGL.timelineStart
    );
    //should return the type of interactive state...
    if (clickedOnScroll === ScrollBarClick.None) {
      dispatch(setInteractiveState(InteractiveState.Pan, id));
    } else if (clickedOnScroll === ScrollBarClick.ClickedOnThumb) {
      dispatch(setInteractiveState(InteractiveState.ScrollBarThumb, id));
    } else {
      dispatch(setInteractiveState(InteractiveState.ScrollBar, id));
    }
  };

  const handleMouseUp = (mouseEvent: React.MouseEvent): void => {
    isAnimating = false;
    // check if where the mouse has ended is in the month(s) of loaded Data
    // const [minTime, maxTime] = waterfallGL?.current?.getTimeRange();
    const newRange = waterfallGL.current.getTimeRange();
    if (newRange) setCurrentRange(waterfallGL.current.getTimeRange());
    //check if you are hovering on thumb bar
    const rect = mount?.current?.getBoundingClientRect();
    waterfallGL?.current?.hoveredOnScrollBarThumb(
      (mouseEvent.clientX - rect.left) * window.devicePixelRatio,
      mouseEvent.clientY - rect.top - BaseTimelineGL.timelineStart
    );

    // interactionMode = InteractiveState.None;
    dispatch(setInteractiveState(InteractiveState.None, id));
    if (!shouldDeselectRef.current) {
      return;
    }

    start();
  };

  const handleMouseMove = (event: React.MouseEvent): void => {
    switch (interactiveState) {
      case InteractiveState.Pan:
        // Only pan the timeline if it isn't locked to an available crew sched window
        if (isAnimating) {
          if (Math.abs(event.movementX) > 3 || Math.abs(event.movementY) > 3) {
            shouldDeselectRef.current = false;
          }
          waterfallGL?.current?.moveCameraByXPixels(-event.movementX);
        }
        break;
      case InteractiveState.ScrollBarThumb:
      case InteractiveState.ScrollBar:
        waterfallGL.current.dragScrollBarThumb(event.movementY);
        break;
      default:
        break;
    }

    //check if hovering on the scroll bar
    const rect = mount?.current?.getBoundingClientRect();
    if (interactiveState !== InteractiveState.ScrollBar && interactiveState !== InteractiveState.ScrollBarThumb) {
      waterfallGL?.current?.hoveredOnScrollBarThumb(
        (event.clientX - rect.left) * window.devicePixelRatio,
        event.clientY - rect.top - BaseTimelineGL.timelineStart
      );
    }

    updateHoveredEvent(event.clientX, event.clientY);
  };

  const handleMouseLeave = (event: any): void => {
    //when you mouse off - set the hovered events to empty
    waterfallGL?.current?.setHoveredEvents([], 0);

    //clear thumb hover color aslong as you aren't dragging the thumb
    if (interactiveState !== InteractiveState.ScrollBarThumb) {
      waterfallGL?.current?.hoveredOnScrollBarThumb(0, 0);
    }

    event.preventDefault();
    if (interactiveState !== InteractiveState.ScrollBarThumb) {
      stopMouseInteraction(event);
    } else {
      if (!mount) {
        return;
      } else if (!captureMode || !pointerId) {
        enterCaptureMode(event.pointerId);
      }
    }
  };

  const stopMouseInteraction = (event: React.MouseEvent): void => {
    shouldDeselectRef.current = true;
    // event.preventDefault();
    if (interactiveState !== InteractiveState.Pan) {
      dispatch(setInteractiveState(InteractiveState.Pan, id));
      //dispatch(setHoveredEvents({ hoveredEvents: [], anchorPos: { x: -1, y: -1 }, windowId: id }));
    }
    isAnimating = false;
  };

  /**
   * item List useEffect - Added new setting to disable expanded row in pairing editor.
   */
  useEffect(() => {
    if (!waterfallGL.current) {
      return;
    }
    waterfallGL?.current?.updateItem(itemList);
    if (!isAnimating) {
      start();
    }
  }, [itemList]);

  /**
   * Pairings useEffect
   */
  useEffect(() => {
    if (pairingMap) {
      waterfallGL?.current?.updatePairings(pairingMap);
    }
  }, [pairingMap]);

  const scrollRowShortCodeIntoView = useCallback(() => {
    if (!waterfallGL.current || !itemListRef.current) {
      return;
    }
    const [yMin] = waterfallGL.current.getYRange();
    // itemListRef.current.scrollTop = yMin; // Old method used to scroll non-virtualized lists.
    // Using the scrollTo method made available by the virtualized List component used in the pairing waterfall list.
    //@ts-ignore
    if (itemListRef?.current?.scrollTo) itemListRef.current.scrollTo(yMin);
  }, [itemListRef]);

  const timelineLink = useSelector((state: IRootState) => state.crewSchedule.timelineLink);
  const timelinesLinked = useMemo(() => !!timelineLink?.activeWindowId, [timelineLink]);

  const handleMouseEnter = useCallback(
    (event: any): void => {
      // when mouse enters the canvas, if the timelines are set in link mode, update the active window to this one.
      if (timelineLink?.activeWindowId && timelineLink?.activeWindowId !== id) {
        dispatch(setTimelineLink(timelineLink.start, timelineLink.end, id));
      }
    },
    [dispatch, id, timelineLink]
  );

  // #region Animation
  const animate = useCallback(() => {
    if (waterfallGL.current) {
      waterfallGL.current.draw();
      scrollRowShortCodeIntoView();
      if (isAnimating) {
        frameIdRef.current = window.requestAnimationFrame(animate);
        if (!!timelineLink?.activeWindowId && timelineLink?.activeWindowId === id) {
          const [start, end] = waterfallGL.current.getTimeRange();
          dispatch(setTimelineLink(start, end, id));
        }
      }
    }
  }, [dispatch, id, scrollRowShortCodeIntoView, timelineLink?.activeWindowId]);

  // If there's linked timeline info and NOT the active timeline, set time axis.
  useEffect(() => {
    if (waterfallGL?.current && !!timelineLink?.activeWindowId && timelineLink?.activeWindowId !== id) {
      const { start, end } = timelineLink;
      waterfallGL?.current?.setTimeAxis(start, end);
      waterfallGL?.current?.draw();
    }
  }, [timelineLink, id]);

  const start = useCallback(() => {
    frameIdRef.current = requestAnimationFrame(animate);
  }, [animate]);

  const updateHoveredTime = useCallback(
    (xAxisPos: number) => {
      //translate the hovered xAxis position to Epoch time
      const clientX = xAxisPos - mount?.current?.getBoundingClientRect()?.left;
      const timeHovered: number = waterfallGL?.current?.pixelCoordsToEpochTime(clientX * window.devicePixelRatio);
      //send to redux the epoch time - from redux 'HoveredTime' component will call setHoveredTime to
      // each timeline that is open
      setTimeout(() => dispatch(setHoveredTime(timeHovered)), 33);
    },
    [dispatch]
  );

  const getCrewMemberAndTimeFromMouse = (clientX: number, clientY: number): [string, number, number] => {
    const rect = mount?.current?.getBoundingClientRect();
    const x = (clientX - rect.left) * window.devicePixelRatio;
    const y = clientY - rect.top;
    const crewId = waterfallGL?.current?.getHoveredCrewMember(x, y);

    const [minTime] = waterfallGL.current.getTimeRange();
    const hoveredTime = minTime + waterfallGL?.current?.pixelCoordsToTimeAxis(x);

    return [crewId, hoveredTime, y];
  };

  const updateHoveredEvent = useCallback(
    (clientX: number, clientY: number) => {
      if (!isAnimating && waterfallGL.current && canUpdateHoveredEvent.current) {
        canUpdateHoveredEvent.current = false;
        setTimeout(() => (canUpdateHoveredEvent.current = true), 33);
        updateHoveredTime(clientX);

        const [waterfallRow, hoveredTime, y] = getCrewMemberAndTimeFromMouse(clientX, clientY);
        const rowId = (waterfallRow as any)?._id;

        if (!rowId) {
          return;
        }
        const hoveredEvents: IHoveredEvent[] = [];

        const crewMemberDim = waterfallGL.current?.getCrewMemberDimensions(rowId);
        if (!crewMemberDim) {
          return;
        }

        const yRange = waterfallGL?.current?.getYRange();
        if (!yRange) return;
        const [yMin] = yRange;

        const scaledTimelineStart = BaseTimelineGL.timelineStart; //* window.devicePixelRatio;
        const currentY = y + yMin - crewMemberDim.rowStart - scaledTimelineStart;
        const pairingData = pairingMap[rowId];

        if (
          pairingData.pairings?.length &&
          currentY >= PairingsGL.pairingYStart &&
          currentY <= PairingsGL.pairingHeight + PairingsGL.pairingYStart
        ) {
          const [hoveredPairing, index] = findPairingsFromTime(
            pairingData.pairings,
            hoveredTime,
            0,
            pairingData.pairings.length - 1
          );
          if (hoveredPairing && index >= 0 && index < pairingData.pairings.length) {
            hoveredEvents.push({
              event: hoveredPairing,
              crewId: rowId,
              isPlanned: false,
            });
            hoveredEvents.push(...getOverlappingEvents(hoveredPairing, index, pairingData.pairings, rowId, false));
          }
        }

        // const hoveredEvents;
        const currHoveredEvents = waterfallGL.current.getHoveredEvents();
        if (currHoveredEvents?.[0]?.event?._id !== hoveredEvents?.[0]?.event?._id) {
          dispatch(setHoveredEvents({ hoveredEvents, anchorPos: { x: clientX, y: clientY + 4 }, windowId: id }));
        }
        waterfallGL.current.setHoveredEvents(hoveredEvents, y);
        if (!isAnimating) {
          start();
        }
      }
    },
    [updateHoveredTime, pairingMap, dispatch, id, start]
  );

  /**
   * RETURN function triggered on unmount of canvas.
   */
  useEffect(() => {
    return () => {
      if (waterfallGL?.current?.loseContextExt) {
        // The following will remove the webgl context on unmount if extension is available.
        waterfallGL.current.loseContextExt.loseContext();
      }
    };
  }, []);

  // #region CrewSchedule Initialize
  useLayoutEffect(() => {
    waterfallGL.current = new WaterfallGL(mount.current, 0, 0);
    if (currentRange) {
      waterfallGL?.current?.setTimeAxis(currentRange[0], currentRange[1]);
      start();
    }
  }, []);

  useEffect(() => {
    waterfallGL?.current?.setTheme(theme.custom.timeline.webglCanvas);
  }, [theme]);

  useEffect(() => {
    if (currentRange) return;
    waterfallGL?.current?.setTimeAxis(initialStartTime, initialEndTime);
  }, [initialStartTime, initialEndTime, currentRange]);

  // Jump to a supplied Date (if not provided, jump to current date)
  const scrollToDate = useCallback(
    (jumpDate: Date, toFront?: boolean): void => {
      const jumpTime =
        typeof jumpDate === "object" && typeof jumpDate.getMonth === "function"
          ? jumpDate.getTime()
          : new Date().getTime();

      // Guards below logic from TypeError if waterfallGL not yet initialized.
      const timeRange = waterfallGL.current.getTimeRange();
      if (!timeRange) return;
      const [currentStart, currentEnd] = timeRange;
      // If jumping to front of an item, apply a 2 percent margin (based on scale of available space)
      const buffer = toFront ? (currentEnd - currentStart) * 0.02 : (currentEnd - currentStart) / 2;
      const currentMidPoint = Math.round((currentEnd - currentStart) / 2);
      const moveDistance = currentMidPoint - (currentMidPoint - jumpTime) - buffer;
      const pixelXDistance = waterfallGL?.current?.timeAxisToPixelCoords(moveDistance);
      waterfallGL?.current?.moveCameraByXPixels(pixelXDistance);

      if (!isAnimating) {
        animate();
      }
    },
    [animate]
  );

  useEffect(() => {
    if (setScrollToDate) setScrollToDate(() => scrollToDate);
  }, [setScrollToDate, scrollToDate]);

  // #region Resize
  useLayoutEffect(() => {
    if (!mount.current) {
      return () => {
        void 0;
      };
    }

    const element = mount.current;
    let rafId = 0;
    let lastWidth = 0;
    let lastHeight = 0;

    const updateViewport = (): void => {
      const rect = element.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return;
      }

      // Only update the WebGL viewport when size actually changed.
      if (rect.width !== lastWidth || rect.height !== lastHeight) {
        lastWidth = rect.width;
        lastHeight = rect.height;
        waterfallGL.current?.setViewport(rect.width, rect.height);
        start();
      }
    };

    const scheduleViewportUpdate = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateViewport);
    };

    // Initial measurement (covers first mount and tab-return cases).
    scheduleViewportUpdate();

    // React to element resize (covers hide/show within tabs, panel resizes, etc).
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => scheduleViewportUpdate());
      resizeObserver.observe(element);
    }

    // Fallback/extra safety.
    window.addEventListener("resize", scheduleViewportUpdate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleViewportUpdate);
    };
  }, [start]);

  // If displayTimezone is anything but UTC, use it's assigned timezoneId, otherwise use crewTime (selected base).
  const glTimezone: IBaseTime = useMemo(() => {
    const baseTime =
      displayTimezone.name !== "UTC"
        ? {
            id: displayTimezone.ident,
            label: displayTimezone.name,
            isUTC: false,
            timezoneId: displayTimezone.timezoneId,
          }
        : crewBase.value;
    return baseTime;
  }, [crewBase, displayTimezone]);

  useEffect(() => {
    waterfallGL?.current?.setBaseTime(glTimezone);
    if (!isAnimating) {
      start();
    }
  }, [glTimezone, start]);

  // If any of the items included here are modified, redraw the webgl component.
  useLayoutEffect(() => {
    if (!isAnimating) start();
  }, [pairingMap, start]);

  return (
    <OCPairingWaterfallCanvas container direction="column">
      <div
        className="crew_schedGL"
        ref={mount}
        tabIndex={0}
        id={props.id + "-waterfallGL"}
        onWheel={handleWheel(
          waterfallGL,
          mount,
          isAnimating,
          start,
          id,
          id === timelineLink?.activeWindowId ? dispatch : null
        )}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onPointerEnter={handleMouseEnter}
        onPointerLeave={handleMouseLeave}
        onPointerUp={() => {
          if (captureMode && mount) {
            exitCaptureMode();
          }
        }}
      >
        <Grid container className="linked">
          <IconButton
            className="link_button"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseUp={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setShowTimelineDatePickers(false);
              if (timelinesLinked) {
                dispatch(setTimelineLink(null)); // disable link mode.
              } else {
                const [start, end] = waterfallGL.current.getTimeRange();
                dispatch(setTimelineLink(start, end, id));
              }
            }}
            tooltipProps={{
              title: timelinesLinked ? "Unlink crew schedule timelines" : "Link all crew schedule timelines to active.",
              enterDelay: 1000,
              placement: "top",
            }}
            size="large"
          >
            {timelinesLinked ? <Link color="primary" /> : <LinkOpen />}
          </IconButton>
        </Grid>
      </div>

      <HoveredCrewSchedEvents
        anchorEl={mount.current}
        selectedCrewEvents={[]} // selected events disabled in waterfall view.
      />

      <FlightTimelineDatePicker
        anchorID={`${props.id}-waterfallGL`}
        timelineGL={waterfallGL.current}
        display={showTimelineDatePickers}
        setDisplay={setShowTimelineDatePickers}
        refreshTimeline={animate}
      />
    </OCPairingWaterfallCanvas>
  );
};
